import { StrictMode, lazy, Suspense, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, useRouteError } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles.css';
import { SessionProvider, useSession } from './lib/session.tsx';
import { ToastProvider } from './components/Toasts.tsx';
import { AppShell } from './layout/AppShell.tsx';
import { Alert, Loading } from './components/ui.tsx';
import { ForgotPasswordPage, LoginPage, SetPasswordPage } from './pages/Auth.tsx';

const Overview = lazy(() => import('./pages/Overview.tsx'));
const Campaigns = lazy(() => import('./pages/Campaigns.tsx'));
const Composer = lazy(() => import('./pages/composer/Composer.tsx'));
const NewCampaign = lazy(() => import('./pages/composer/NewCampaign.tsx'));
const Report = lazy(() => import('./pages/Report.tsx'));
const Reports = lazy(() => import('./pages/Reports.tsx'));
const Contacts = lazy(() => import('./pages/contacts/Contacts.tsx'));
const AudioLibrary = lazy(() => import('./pages/audio/AudioLibrary.tsx'));
const Settings = lazy(() => import('./pages/settings/Settings.tsx'));
const Account = lazy(() => import('./pages/Account.tsx'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 5_000 } },
});

function RequireSession({ children }: { children: ReactNode }) {
  const { me, loading } = useSession();
  if (loading) return <Loading label="Starting…" />;
  if (!me) return <Navigate to="/login" replace />;
  if (!me.workspaces.length)
    return <Alert tone="warning">Your account has no workspace. Contact the administrator.</Alert>;
  return <>{children}</>;
}

function GuestOnly({ children }: { children: ReactNode }) {
  const { me, loading } = useSession();
  if (loading) return <Loading />;
  return me ? <Navigate to="/" replace /> : children;
}

function RouteError() {
  const err = useRouteError() as Error;
  return (
    <div className="p-8">
      <Alert tone="danger" title="This page could not be shown">
        {err?.message ?? 'Unexpected error'}. Reload the page; if it keeps happening, the error has been
        logged on the server.
      </Alert>
    </div>
  );
}

const page = (el: ReactNode) => <Suspense fallback={<Loading />}>{el}</Suspense>;

const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <GuestOnly>
        <LoginPage />
      </GuestOnly>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <GuestOnly>
        <ForgotPasswordPage />
      </GuestOnly>
    ),
  },
  { path: '/reset-password', element: <SetPasswordPage purpose="reset" /> },
  { path: '/setup', element: <SetPasswordPage purpose="setup" /> },
  {
    path: '/',
    element: (
      <RequireSession>
        <AppShell />
      </RequireSession>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: page(<Overview />) },
      { path: 'campaigns', element: page(<Campaigns />) },
      { path: 'campaigns/new', element: page(<NewCampaign />) },
      { path: 'campaigns/:id/edit', element: page(<Composer />) },
      { path: 'campaigns/:id', element: page(<Report />) },
      { path: 'reports', element: page(<Reports />) },
      { path: 'contacts', element: page(<Contacts />) },
      { path: 'audio', element: page(<AudioLibrary />) },
      { path: 'settings', element: page(<Settings />) },
      { path: 'settings/:tab', element: page(<Settings />) },
      { path: 'account', element: page(<Account />) },
      { path: '*', element: <Alert tone="warning">Page not found.</Alert> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
